import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Button, Eyebrow, Reveal, SectionHeading, Section, Marquee, CTASection, ArrowLink } from "@/components/ui-bits";
import { ProductCard, CapabilityCard, ProcessStep, FlowDiagram, CertCard, PhotoPlaceholder } from "@/components/shared";
import { IMG, PRODUCTS, IDEA_STEPS, CAPABILITIES, CREDIBILITY, WHY, PROJECT_JOURNEY, BUILD_OPTIONS, CERTS, CONSULTATION, BEYOND_FLOW } from "@/lib/data";
import { ArrowRight, Check } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const HeroLine = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease, delay }}>
      {children}
    </motion.span>
  </span>
);

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "0%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative px-5 sm:px-8 lg:px-14 pt-32 sm:pt-36 lg:pt-40 pb-10">
      <div className="max-w-[1400px] mx-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <Eyebrow>Beverage &amp; Functional Product Manufacturing</Eyebrow>
        </motion.div>

        <h1 data-testid="home-title" className="display-hero mt-8 text-[clamp(3rem,9.5vw,8.5rem)] leading-[1.05] text-ws-dark">
          <HeroLine delay={0.25}>From idea</HeroLine>
          <HeroLine delay={0.38}><span className="italic text-ws-accent">to shelf.</span></HeroLine>
        </h1>

        <div className="grid lg:grid-cols-12 gap-10 mt-10 lg:mt-14 items-end">
          <div className="lg:col-span-5">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
              className="text-lg sm:text-xl text-ws-text2 leading-relaxed max-w-xl">
              We help ambitious brands bring beverage mixes, hydration products, energy stick packs and
              functional products to life — from formulation and sampling to manufacturing and finished packaging.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.72, duration: 0.8 }}
              className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Button to="/request-a-quote" testid="hero-request-quote">Request a Quote</Button>
              <Button to="/capabilities" variant="outline" testid="hero-explore">Explore Capabilities</Button>
            </motion.div>
          </div>

          <motion.div className="lg:col-span-7 relative rounded-3xl overflow-hidden aspect-[16/10] bg-ws-bg2"
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1, ease }}>
            <motion.img src={IMG.hero} alt="Conceptual unbranded beverage stick packs and sachets in a premium manufacturing setting"
              style={{ y, scale }} className="w-full h-[115%] object-cover" />
            <span className="absolute bottom-4 left-4 label text-[0.58rem] bg-ws-bg/85 backdrop-blur text-ws-dark rounded px-2.5 py-1.5">
              Conceptual product imagery
            </span>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
          className="mt-12 pt-8 border-t border-ws-border flex flex-wrap items-center gap-x-3 gap-y-2">
          {["Formulation", "Blending", "Filling", "Sachet Packaging"].map((c, i) => (
            <React.Fragment key={c}>
              <span className="label text-ws-dark">{c}</span>
              {i < 3 && <span className="text-ws-accent">•</span>}
            </React.Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  useSEO(
    "Wisperwick Studio | Beverage Mix & Sachet Manufacturing | From Idea to Shelf",
    "Wisperwick Studio helps emerging and established brands develop and manufacture energy drink mixes, hydration products, electrolyte drinks, wellness mixes and single-serve sachets from idea to shelf."
  );

  return (
    <div>
      <Hero />

      {/* Trust / credibility */}
      <Section bg="var(--ws-bg2)">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Built for brands ready to build" title="Development and manufacturing under one partner." />
            <Reveal delay={0.1}><p className="mt-6 text-ws-text2 leading-relaxed max-w-lg">From first formulation to finished product, Wisperwick Studio brings development and manufacturing capabilities together under one partner.</p></Reveal>
          </div>
          <div className="lg:col-span-7 grid min-[380px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-3">
            {CREDIBILITY.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <div className="bg-ws-card border border-ws-border rounded-xl px-5 py-6 h-full flex items-center">
                  <span className="label text-ws-dark leading-snug">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* What can we help you make */}
      <Section>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <SectionHeading eyebrow="Product categories" title="What can we help you make?" />
          <Reveal delay={0.1}><p className="text-ws-text2 max-w-sm text-sm leading-relaxed">Product suitability depends on formulation, ingredients, regulatory requirements and production specifications.</p></Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRODUCTS.map((p, i) => <ProductCard key={p.title} item={p} index={i} />)}
          <Reveal delay={0.16}>
            <Link to="/request-a-quote" data-testid="custom-mix-card" className="group h-full min-h-[260px] rounded-2xl border border-dashed border-ws-accent bg-ws-lightAccent/40 p-8 flex flex-col justify-between transition-colors hover:bg-ws-lightAccent">
              <div>
                <span className="label text-ws-accentHover">Custom Beverage Mixes</span>
                <h3 className="font-display text-3xl text-ws-dark mt-4 leading-tight">Have a completely different idea?</h3>
                <p className="text-ws-text2 mt-3 text-sm">Tell us what you're building.</p>
              </div>
              <span className="inline-flex items-center gap-2 label text-ws-dark mt-6">Start Your Product <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          </Reveal>
        </div>
      </Section>

      {/* Why Wisperwick Studio exists */}
      <section className="px-5 sm:px-8 lg:px-14 py-24 lg:py-36" style={{ background: "var(--ws-dark)" }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal><Eyebrow>Why Wisperwick Studio exists</Eyebrow></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-display text-ws-bg text-4xl sm:text-5xl lg:text-[4rem] leading-[0.98] mt-7 max-w-4xl text-balance">
              Great product ideas shouldn't get stuck in a notebook.
            </h2>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-12 mt-14">
            <div className="lg:col-span-6">
              <Reveal delay={0.1}><p className="text-ws-bg/70 text-lg leading-relaxed">Many young brands have the idea, the audience and the ambition — but not the manufacturing infrastructure to bring the product to life. <span className="text-ws-bg">Wisperwick Studio exists to bridge that gap.</span></p></Reveal>
            </div>
            <div className="lg:col-span-6 flex flex-col gap-4">
              {['"I have an idea."', '"I have a product."', '"I have something ready to launch."'].map((t, i) => (
                <Reveal key={t} delay={0.15 + i * 0.1}>
                  <div className="flex items-center gap-4">
                    <span className="font-display text-ws-accent text-2xl w-8">0{i + 1}</span>
                    <p className="font-display text-2xl sm:text-3xl text-ws-bg italic">{t}</p>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.5}><div className="mt-6"><Button to="/request-a-quote" variant="accent" testid="bring-idea">Bring Us Your Idea</Button></div></Reveal>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["Energy Stick Packs", "Hydration Mixes", "Electrolyte Sachets", "Wellness Drink Mixes", "Functional Beverages", "Private Label"]} />

      {/* Idea to shelf */}
      <Section>
        <SectionHeading eyebrow="Idea to shelf" title="You bring the idea. We help build the product." sub="You shouldn't need to build a factory before you can build a brand." />
        <div className="grid lg:grid-cols-2 gap-x-16 gap-y-0 mt-16">
          <div>{IDEA_STEPS.slice(0, 3).map((s, i) => <ProcessStep key={s.n} step={s} index={i} last={false} />)}</div>
          <div>{IDEA_STEPS.slice(3).map((s, i) => <ProcessStep key={s.n} step={s} index={i} last={i === 2} />)}</div>
        </div>
        <Reveal><div className="mt-6"><FlowDiagram items={["Idea", "Product", "Brand", "Shelf"]} /></div></Reveal>
      </Section>

      {/* Consultation layer — extension of Idea to Shelf */}
      <section className="px-5 sm:px-8 lg:px-14 py-24 lg:py-36" style={{ background: "var(--ws-dark)" }}>
        <div className="max-w-[1400px] mx-auto">
          <Reveal><Eyebrow>Beyond manufacturing</Eyebrow></Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <Reveal delay={0.05}>
              <h2 className="font-display text-ws-bg text-4xl sm:text-5xl lg:text-[3.6rem] leading-[0.98] mt-7 max-w-3xl text-balance">
                From idea to shelf — <span className="italic text-ws-accent">and beyond.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-ws-bg/70 max-w-sm text-sm leading-relaxed">
                We don't stop at the finished product. From concept to commercial growth, we stay a partner through every stage.
              </p>
            </Reveal>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
            {CONSULTATION.map((c, i) => <CapabilityCard key={c.title} item={c} index={i} dark />)}
            <Reveal delay={0.16}>
              <div className="h-full min-h-[220px] rounded-2xl border border-ws-accent/40 bg-ws-accent/10 p-8 flex flex-col justify-between">
                <p className="font-display text-2xl text-ws-bg leading-tight">Ready to build something that scales?</p>
                <div className="mt-6"><Button to="/request-a-quote" variant="accent" testid="consultation-cta">Talk to Our Team</Button></div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}><div className="mt-14"><FlowDiagram items={BEYOND_FLOW} dark /></div></Reveal>
        </div>
      </section>

      {/* Manufacturing capabilities */}
      <Section bg="var(--ws-bg2)">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <SectionHeading eyebrow="Manufacturing capabilities" title="Built around the product." />
          <Reveal delay={0.1}><ArrowLink to="/capabilities" testid="home-capabilities-link">View all capabilities</ArrowLink></Reveal>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CAPABILITIES.map((c, i) => <CapabilityCard key={c.title} item={c} index={i} />)}
        </div>
      </Section>

      {/* Energy stick pack feature */}
      <Section>
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <SectionHeading eyebrow="Modern product formats" title="Small format. Big opportunity." sub="Single-serve energy and functional drink mixes give brands a convenient format designed for modern, on-the-go consumers." />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-8 max-w-md">
              {["Portable", "Convenient", "Single serve", "Easy to carry", "Easy to distribute", "Ecommerce & retail"].map((b, i) => (
                <Reveal key={b} delay={i * 0.05}>
                  <div className="flex items-center gap-2.5 text-sm text-ws-dark">
                    <Check className="w-4 h-4 text-ws-accent shrink-0" strokeWidth={2.4} /> {b}
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}><div className="mt-10"><Button to="/request-a-quote" testid="discuss-stickpack">Discuss Your Stick Pack</Button></div></Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-ws-bg2">
              <img src={IMG.stickRow} alt="Row of conceptual unbranded single-serve stick packs" loading="lazy" className="w-full h-full object-cover" />
              <span className="absolute bottom-4 left-4 label text-[0.58rem] bg-ws-bg/85 backdrop-blur text-ws-dark rounded px-2.5 py-1.5">Conceptual product imagery</span>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* Facility (placeholder) */}
      <Section bg="var(--ws-bg2)">
        <SectionHeading eyebrow="The facility" title="Where ideas become products." />
        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          <Reveal><PhotoPlaceholder src={IMG.facility1} alt="Wisperwick Studio blending and sieving equipment" label="WISPERWICK STUDIO — BLENDING & SIEVING" /></Reveal>
          <Reveal delay={0.08}><PhotoPlaceholder src={IMG.facility2} alt="Wisperwick Studio stick pack packaging area" label="WISPERWICK STUDIO — PACKAGING AREA" /></Reveal>
        </div>
        <Reveal delay={0.1}><div className="mt-12"><FlowDiagram items={["Raw Materials", "Weighing", "Blending", "Filling", "Packaging", "Quality Control"]} /></div></Reveal>
      </Section>

      {/* Quality */}
      <Section>
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <SectionHeading eyebrow="Quality" title="Quality isn't a final check. It's the process." sub="Quality begins before production and continues throughout the manufacturing process." />
            <Reveal delay={0.15}><div className="mt-8"><ArrowLink to="/quality" testid="home-quality-link">Explore our quality system</ArrowLink></div></Reveal>
          </div>
          <div className="lg:col-span-6 grid min-[380px]:grid-cols-2 gap-3">
            {["Hygiene", "Process control", "Batch consistency", "Documentation", "Traceability", "Food safety"].map((q, i) => (
              <Reveal key={q} delay={i * 0.05}>
                <div className="border border-ws-border rounded-xl p-5 bg-ws-card h-full">
                  <span className="label text-ws-dark">{q}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div data-testid="home-standards" className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-10 sm:mt-14">
          {CERTS.map((c) => <CertCard key={c} name={c} />)}
        </div>
      </Section>

      {/* OEM / ODM / Private label */}
      <Section bg="var(--ws-bg2)">
        <SectionHeading eyebrow="How you build" title="Choose how you want to build." />
        <div className="grid lg:grid-cols-3 gap-5 mt-14">
          {BUILD_OPTIONS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.08}>
              <div className="bg-ws-dark rounded-2xl p-8 h-full flex flex-col">
                <span className="font-display text-ws-accent text-lg">0{i + 1}</span>
                <h3 className="font-display text-3xl text-ws-bg mt-4">{o.title}</h3>
                <p className="text-ws-bg mt-5 font-medium">{o.lead}</p>
                <p className="text-ws-bg/60 text-sm mt-3 leading-relaxed flex-1">{o.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}><div className="mt-10"><Button to="/request-a-quote" testid="oem-talk-team">Talk to Our Team</Button></div></Reveal>
      </Section>

      {/* Why choose us */}
      <Section>
        <SectionHeading eyebrow="Why brands choose us" title="More than a factory." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-14">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={(i % 3) * 0.07}>
              <div className="border-t border-ws-border pt-6 h-full">
                <span className="font-display text-ws-accent text-xl">0{i + 1}</span>
                <h3 className="font-display text-2xl text-ws-dark mt-3 leading-tight">{w.title}</h3>
                <p className="text-ws-text2 text-sm mt-3 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Project journey */}
      <Section bg="var(--ws-bg2)">
        <SectionHeading eyebrow="The process" title="What happens after you contact us?" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 mt-14">
          {PROJECT_JOURNEY.map((s, i) => (
            <Reveal key={s.n} delay={(i % 3) * 0.08}>
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-display text-4xl text-ws-dark">{s.n}</span>
                  <span className="h-px flex-1 bg-ws-border" />
                </div>
                <h3 className="font-display text-2xl text-ws-dark mt-4">{s.title}</h3>
                <p className="text-ws-text2 text-sm mt-2 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}><div className="mt-12"><Button to="/request-a-quote" testid="journey-start-project">Start Your Project</Button></div></Reveal>
      </Section>

      <CTASection />
    </div>
  );
}
